/**
 * Whether the user is actually looking at this tab right now.
 *
 * Inactive the moment the tab is hidden (switched to another tab, minimised, the
 * screen locked) — an open-but-backgrounded tab does not count — and after
 * IDLE_MS with no mouse, keyboard, touch or scroll input while it is visible.
 * Drives the "who else is here" avatars other users see (see ActiveViewers).
 */

import { useEffect, useState } from 'react';

/** No input for this long and the user is counted as away. */
export const IDLE_MS = 5 * 60 * 1000;

const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

export function useUserActivity(idleMs: number = IDLE_MS): boolean {
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible');
  const [idle, setIdle] = useState(false);

  useEffect(() => {
    let timer: number | undefined;
    // pointermove fires constantly; re-arming the timer at most once a second is plenty.
    let lastInput = 0;

    const arm = () => {
      if (timer) clearTimeout(timer);
      timer = window.setTimeout(() => setIdle(true), idleMs);
    };
    const onInput = () => {
      const now = Date.now();
      if (now - lastInput < 1000) return;
      lastInput = now;
      setIdle(false);
      arm();
    };
    const onVisibility = () => {
      const isVisible = document.visibilityState === 'visible';
      setVisible(isVisible);
      if (isVisible) {
        // Coming back to the tab is itself activity.
        lastInput = Date.now();
        setIdle(false);
        arm();
      }
    };

    arm();
    for (const e of INPUT_EVENTS) window.addEventListener(e, onInput, { passive: true, capture: true });
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (timer) clearTimeout(timer);
      for (const e of INPUT_EVENTS) window.removeEventListener(e, onInput, { capture: true });
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [idleMs]);

  return visible && !idle;
}
